<template>
    <div class="price-tag" :class="`price-tag--${variant}`">
        <div v-if="context.isLoyalty" class="price-tag__banner">
            {{ tagText('onlyWith', { loyaltyLabel: tagText('loyaltyLabel') }) }}
        </div>
        <div class="price-tag__body">
            <slot />
        </div>
        <div v-if="limitText" class="price-tag__limit">{{ limitText }}</div>
        <div v-if="customerLimitText" class="price-tag__limit">{{ customerLimitText }}</div>
        <div class="price-tag__validity">
            {{ tagText('valid', context.validity) }}
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { isNil } from 'lodash';
import { usePriceTagText } from '@/js/composables/usePriceTagText';

const props = defineProps({
    context: {
        type: Object,
        required: true,
    },
    variant: {
        type: String,
        default: 'standard',
    },
});

const { tagText } = usePriceTagText();

const limitText = computed(() => {
    const { purchaseLimitUnits, freeItemsLimit } = props.context;
    if (isNil(purchaseLimitUnits)) return null;
    if (!isNil(freeItemsLimit)) {
        return tagText('limitUnitsWithFree', { limit: purchaseLimitUnits, free: freeItemsLimit });
    }
    return tagText('limitPerReceipt', { limit: purchaseLimitUnits });
});

const customerLimitText = computed(() => {
    const { customerLimitUnits } = props.context;
    return isNil(customerLimitUnits)
        ? null
        : tagText('limitPerCustomer', { limit: customerLimitUnits });
});
</script>

<style lang="scss" scoped>
.price-tag {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    width: 52rem;
    min-height: 30rem;
    padding: 1.6rem;
    background: var(--price-tag-background);
    color: var(--price-tag-text);
    font-family: 'Lato', 'Source Sans Pro', Arial, sans-serif;
    font-weight: 800;
    line-height: 1.1;
    box-sizing: border-box;

    &--split {
        border: 0.6rem solid var(--price-tag-background);
        background: var(--price-tag-split-highlight);
        padding: 0;
        gap: 0;
    }

    &__banner,
    &__validity {
        background: var(--price-tag-accent);
        color: var(--price-tag-accent-text);
        border-radius: 0.4rem;
        text-align: center;
        text-transform: uppercase;
        padding: 0.6rem 1rem;
    }

    &__banner {
        font-size: 3rem;
    }

    &__body {
        flex: 1;
        display: flex;
        flex-direction: column;
    }

    &__limit {
        font-size: 1.6rem;
    }

    &__validity {
        font-size: 1.4rem;
    }

    &--split &__banner,
    &--split &__limit,
    &--split &__validity {
        margin: 0.8rem 1.2rem;
    }
}
</style>
